import type { ReactNode } from 'react';

type SpinnerSize = 'sm' | 'md' | 'lg';

interface SpinnerProps {
  size?: SpinnerSize;
  className?: string;
  /** Optional label shown below the spinner */
  label?: ReactNode;
  /** Render without the h-64 centering wrapper (for use inside buttons etc.) */
  inline?: boolean;
  /** Accessible label announced by screen readers; defaults to "Loading" */
  ariaLabel?: string;
}

const sizeClasses: Record<SpinnerSize, string> = {
  sm: 'w-4 h-4 border-2',
  md: 'w-8 h-8 border-4',
  lg: 'w-12 h-12 border-4',
};

export function Spinner({ size = 'md', className = '', label, inline, ariaLabel = 'Loading' }: SpinnerProps) {
  if (inline) {
    return (
      <div
        role="status"
        aria-label={ariaLabel}
        className={`${sizeClasses[size]} border-current border-t-transparent rounded-full animate-spin`}
      />
    );
  }

  return (
    <div
      role="status"
      aria-label={typeof label === 'string' ? label : ariaLabel}
      aria-live="polite"
      className={`flex items-center justify-center h-64 ${className}`}
    >
      <div className="text-center">
        <div
          aria-hidden="true"
          className={`${sizeClasses[size]} border-primary-600 border-t-transparent rounded-full animate-spin mx-auto`}
        />
        {label && <p className="mt-4 text-surface-600">{label}</p>}
      </div>
    </div>
  );
}

export default Spinner;
